import {
  DefaultSchoolYear,
  EvaluationType,
  FilterKeys,
  FilterSection,
  GetReportsQueryFilters,
  REPORTS_QUERY_FILTER_KEYS,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  DateRangeDropdown,
  EvaluationTypeExpandedSelect,
  FiltersSidebarMethods,
  PersonalFiltersDropdown,
  pickOnlyParamsFromFilterKeys,
  renderAreaOfLearningTags,
  ReportStatusTagSelect,
  UserTagSelect,
} from '@schooly/components/filters';
import {
  FilterDropdown,
  FiltersContainer,
  ReportStatusExpandedSelect,
  UserExpandedSelect,
} from '@schooly/components/filters';
import { TagSelect } from '@schooly/style';
import { FC, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';

import { ReportsFiltersSidebar } from './ReportsFiltersSidebar';
import { useReportsFilters } from './useReportsFilters';

type ReportsFiltersProps = {
  schoolId: string;
  filters: GetReportsQueryFilters;
  defaultFilters: GetReportsQueryFilters;
  onSetFilters: (v: GetReportsQueryFilters) => void;
  notActualInitialDate?: boolean;
  defaultSchoolYear?: DefaultSchoolYear;
  defaultUserFilters: GetReportsQueryFilters;
};

export const ReportsFilters: FC<ReportsFiltersProps> = ({
  defaultFilters,
  onSetFilters,
  filters,
  schoolId,
  notActualInitialDate,
  defaultSchoolYear,
}) => {
  const sidebar = useRef<FiltersSidebarMethods | null>(null);
  const personalFiltersDropdown = useRef<PersonalFiltersDropdown | null>(null);
  const { currentStaff } = useAuth();
  const { formatMessage } = useIntl();

  const {
    labels,
    filtersDate,
    filtersReportStatus,
    filtersStaff,
    filtersEvaluationType,
    filtersAreaOfLearning,
    onSetDate,
    onSetReportStatus,
    onClearReportStatus,
    onSetStaff,
    onClearStaff,
    onSetEvaluationType,
    onClearEvaluationType,
    onSetAreaOfLearning,
  } = useReportsFilters({ filters, onSetFilters });

  const handleSaveFilter = useCallback(() => {
    personalFiltersDropdown.current?.saveFilter();
  }, []);

  const dateFilter = { [FilterKeys.Date]: filtersDate };
  const isTutorFeedbackSelected = filtersEvaluationType?.includes(EvaluationType.TutorFeedback);

  return (
    <FiltersContainer onOpenFilters={() => sidebar.current?.open()}>
      <PersonalFiltersDropdown
        ref={personalFiltersDropdown}
        currentUser={currentStaff}
        relationId={currentStaff?.relation_id || ''}
        onSaveFilter={handleSaveFilter}
        schoolId={schoolId}
        section={FilterSection.Report}
        filters={filters}
        defaultSchoolYear={defaultSchoolYear}
        onSetFilters={(v) => {
          onSetFilters(pickOnlyParamsFromFilterKeys(REPORTS_QUERY_FILTER_KEYS, v));
        }}
      />

      {filtersDate && (
        <DateRangeDropdown
          schoolId={schoolId}
          date={filtersDate}
          onSetDate={onSetDate}
          openLabel={labels[FilterKeys.Date]}
          defaultSchoolYear={defaultSchoolYear}
          notActualInitialDate={notActualInitialDate}
        />
      )}

      {!!filtersReportStatus?.length && (
        <FilterDropdown
          onClear={onClearReportStatus}
          label={labels[FilterKeys.ReportStatus]}
          tags={(open) =>
            filtersReportStatus.map((status) => (
              <ReportStatusTagSelect
                key={status}
                sx={{ maxWidth: 200 }}
                status={status}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <ReportStatusExpandedSelect
              selectedStatuses={filtersReportStatus}
              onApply={onSetReportStatus}
              onClose={onClose}
              onClear={onClearReportStatus}
            />
          )}
        </FilterDropdown>
      )}

      {!!filtersStaff?.length && (
        <FilterDropdown
          onClear={onClearStaff}
          label={labels[FilterKeys.Staff]}
          tags={(open) =>
            filtersStaff.map((id) => (
              <UserTagSelect
                userType="staff"
                currentUser={currentStaff}
                sx={{ maxWidth: 200 }}
                schoolId={schoolId}
                key={id}
                id={id}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <UserExpandedSelect
              type="staff"
              schoolId={schoolId}
              currentUser={currentStaff}
              selectedIds={filtersStaff}
              onApply={onSetStaff}
              onClose={onClose}
              onClear={onClearStaff}
              filters={dateFilter}
            />
          )}
        </FilterDropdown>
      )}

      {!!(filtersEvaluationType?.length || filtersAreaOfLearning?.length) && (
        <FilterDropdown
          onClear={onClearEvaluationType}
          label={labels[FilterKeys.EvaluationType]}
          tags={(open) => [
            ...(isTutorFeedbackSelected
              ? [
                  <TagSelect
                    key="tutor-feedback"
                    label={formatMessage({ id: 'assessments-TutorsFeedback' })}
                    sx={{ maxWidth: 200 }}
                    onClick={open}
                  />,
                ]
              : []),
            ...renderAreaOfLearningTags({
              ids: filtersAreaOfLearning ?? [],
              onClick: open,
              tagProps: { schoolId, sx: { maxWidth: 200 } },
            }),
          ]}
        >
          {(onClose) => (
            <EvaluationTypeExpandedSelect
              schoolId={schoolId}
              selectedEvaluationTypes={filtersEvaluationType ?? []}
              selectedAreaOfLearningIds={filtersAreaOfLearning ?? []}
              onApply={(type, ids) => {
                onSetEvaluationType(type);
                onSetAreaOfLearning(ids);
              }}
              onClose={onClose}
              onClear={onClearEvaluationType}
            />
          )}
        </FilterDropdown>
      )}

      <ReportsFiltersSidebar
        ref={sidebar}
        defaultSchoolYear={defaultSchoolYear}
        defaultFilters={defaultFilters}
        filters={filters}
        onApply={onSetFilters}
        schoolId={schoolId}
      />
    </FiltersContainer>
  );
};
